import { useEffect, useState } from 'react'
import { has, text, type ContentMap } from '../api/content'
import { SocialFooterLinks } from './SocialLinks'

/**
 * The studio's Facebook timeline, embedded on the homepage (D-130).
 *
 * **Why it exists.** The public site is evergreen and the studio posts several
 * times a week — so a visitor's real question, *"is this place active?"*, was
 * being answered by a page that had not changed since it was written. A feed
 * answers it for free and needs nobody to remember anything.
 *
 * **Why Facebook and not Instagram.** Instagram's Basic Display API was shut
 * off in December 2024; the replacement needs a Business account, an app, and
 * tokens that expire — a maintenance task pointed at ISI rather than at the
 * studio, which is the same anti-pattern in a different coat. Facebook's Page
 * Plugin needs none of that.
 *
 * **Why the plain iframe rather than Meta's SDK.** No `connect.facebook.net`
 * script on the site, far less tracking of the studio's visitors, and it can be
 * lazily loaded so it costs nothing until somebody scrolls to it.
 */

/**
 * Meta's own bounds. The layout is built around them rather than fighting them.
 *
 * **The plugin draws at the width it is told and no other** (D-258). It is a
 * cross-origin iframe, so it cannot see the box it has been put in, and
 * `adapt_container_width` is honoured by *Meta's JavaScript SDK* measuring the
 * container and rewriting the URL — which is the very script this deliberately
 * does not load. With a plain iframe the parameter does nothing, so a timeline
 * drawn at 500 inside a 300px frame is simply cut off down the right-hand edge,
 * and `scrolling="no"` means there is no way to reach the rest of it. Measured
 * on a 390px handset: frame 300px, contents 500px.
 *
 * So we do the SDK's job ourselves — measure, then tell it. 500 is a hard
 * ceiling Meta enforces; below 180 the plugin refuses to render.
 */
const FEED_WIDTH = 500
const FEED_MIN_WIDTH = 180
const FEED_HEIGHT = 640

/**
 * How wide the timeline should be drawn, measured from the box it sits in.
 *
 * **Quantised to 10px** because every change is a fresh page load from
 * Facebook: dragging a window edge would otherwise refetch the timeline on
 * every animation frame. A residue of up to 9px of Facebook's own white inside
 * a white tile is invisible; a clipped post is not, which is why this rounds
 * *down* and never up.
 */
function useDrawnWidth(box: HTMLElement | null): number | null {
  const [width, setWidth] = useState<number | null>(null)

  useEffect(() => {
    if (!box) return

    const measure = () => {
      const available = box.clientWidth
      if (available <= 0) return

      const drawn = Math.max(
        FEED_MIN_WIDTH,
        Math.min(FEED_WIDTH, Math.floor(available / 10) * 10),
      )
      setWidth((current) => (current === drawn ? current : drawn))
    }

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(box)
    return () => observer.disconnect()
  }, [box])

  return width
}

/**
 * The framed timeline on its own, with no marketing copy around it.
 *
 * Used by the parent and teacher portals (D-130 extended). **Closures and
 * studio news go to Facebook today**, so mirroring it inside the app gives both
 * audiences somewhere to look when they missed a notification, disabled them,
 * or never turned them on — which matters more now that push is opt-in on
 * iPhone (D-133) and SMS is gone (D-132).
 *
 * Returns nothing when the studio has no Facebook address, so a tab that would
 * be empty simply never appears.
 */
export function FacebookEmbed({ content }: { content: ContentMap | undefined }) {
  /*
   * A callback ref rather than `useRef`, so the measurement runs when the box
   * mounts. A ref object does not re-render, so the effect would read `null`
   * on the only pass it ever made.
   */
  const [box, setBox] = useState<HTMLDivElement | null>(null)
  const drawn = useDrawnWidth(box)

  const pageUrl = text(content, 'social.facebook')
  if (!pageUrl) return null

  /*
   * Centred, because 500 is Meta's ceiling and the panel around it is 960px on
   * a laptop now — left-aligned it sat in the corner of a wide empty card
   * (D-300).
   *
   * `w-full` stays. An auto margin beside a `max-width` inside a flex column is
   * exactly the shrink-wrap D-254 is about, and this is the one change that
   * would introduce it.
   */
  return (
    <figure className="m-0 mx-auto w-full max-w-[500px]">
      <div className="overflow-hidden rounded-2xl border border-line-2 bg-white p-2">
        {/*
          The measured box. Holds its height from the start so the page does not
          jump by 640px between the first paint and the first measurement.
        */}
        <div ref={setBox} style={{ minHeight: FEED_HEIGHT }}>
          {drawn !== null && (
            /*
              Keyed on the width so a resize builds a *new* element. Assigning
              `src` to a live iframe pushes an entry onto session history, and a
              back button that needs pressing four times to leave the page is a
              worse bug than the one being fixed.
            */
            <iframe
              key={drawn}
              title="Cabot Dance Academy on Facebook"
              src={embedSrc(pageUrl, drawn)}
              width={drawn}
              height={FEED_HEIGHT}
              loading="lazy"
              scrolling="no"
              frameBorder="0"
              allowFullScreen
              allow="clipboard-write; encrypted-media; picture-in-picture; web-share"
              style={{ width: drawn }}
              className="mx-auto block max-w-full rounded-xl"
            />
          )}
        </div>
      </div>

      <figcaption className="mt-2 text-[12px] text-ink-3">
        Straight from the studio&rsquo;s Facebook page
      </figcaption>
    </figure>
  )
}

function embedSrc(pageUrl: string, width: number): string {
  return (
    `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(pageUrl)}` +
    `&tabs=timeline&width=${width}&height=${FEED_HEIGHT}` +
    '&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true'
  )
}

export default function FacebookFeed({ content }: { content: ContentMap | undefined }) {
  const pageUrl = text(content, 'social.facebook')

  /*
   * Blank heading means the whole section is off — the same switch as the
   * announcement banner, and the reason it is a *content* field rather than a
   * deploy: this is on Meta's roadmap, not ours, and the day it starts
   * misbehaving Tonya can remove it herself.
   */
  if (!has(content, 'home.feed.heading') || !pageUrl) return null

  return (
    <section className="border-y border-line bg-surface">
      <div className="mx-auto max-w-[56rem] px-5 py-14">
        <div className="grid items-start gap-10 md:grid-cols-[1fr_minmax(0,500px)]">
          {/*
            Left stands alone. If Meta blocks the frame, the visitor throttles
            it, or the plugin is finally retired, this is still a complete
            section rather than a heading over an empty box.
          */}
          <div>
            <h2 className="font-display text-2xl text-balance">
              {text(content, 'home.feed.heading')}
            </h2>

            {has(content, 'home.feed.body') && (
              <p className="mt-3 max-w-[34rem] text-[15px] leading-relaxed text-ink-2">
                {text(content, 'home.feed.body')}
              </p>
            )}

            <div className="mt-5">
              <SocialFooterLinks content={content} />
            </div>

            {has(content, 'home.tagline') && (
              <p className="mt-5 font-display text-lg text-accent-2">
                {text(content, 'home.tagline')}
              </p>
            )}
          </div>

          {/*
            Framed on a white tile deliberately, the same treatment the logo
            gets. The plugin is an iframe whose insides cannot be styled — it is
            Facebook's white, Facebook's fonts — so on the dark theme it would
            otherwise read as a hole punched in the page. Given a border and a
            caption it reads as a window instead, which is what it is.
          */}
          <div className="w-full justify-self-center md:justify-self-end">
            <FacebookEmbed content={content} />
          </div>
        </div>
      </div>
    </section>
  )
}
